import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { SegmentService } from '../../../../core/services/segment.service';
import { UserService } from '../../../../core/services/user.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { NumberInputComponent } from '../../../../shared/ui/forms/number-input/number-input.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import {
  ButtonGroupComponent,
  ButtonGroupOption,
} from '../../../../shared/ui/forms/button-group/button-group.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { LoadWithView } from '../../../../core/models/load.model';
import { LoadSegment } from '../../../../core/models/load-segment.model';
import { calculateTotalLoadedMiles } from '../../../../core/utils/itinerary-reconciler';

export type LegPayCalculationMode = 'auto' | 'override';

@Component({
  selector: 'app-leg-pay-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    NumberInputComponent,
    InputComponent,
    ButtonGroupComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
  ],
  templateUrl: './leg-pay-modal.component.html',
})
export class LegPayModalComponent implements OnInit {
  readonly hasCustomActions = true;

  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastService);

  readonly isSaving = signal(false);
  readonly payMode = signal<LegPayCalculationMode>('auto');
  readonly hasExistingDeadhead = signal(false);

  load!: LoadWithView;
  segments = signal<LoadSegment[]>([]);
  targetSegment = signal<LoadSegment | null>(null);
  driverId = signal<string>('');
  form!: FormGroup;

  readonly payModeOptions: ButtonGroupOption[] = [
    { value: 'auto', label: 'Auto (Driver Pay Structure)', icon: 'Sparkles' },
    { value: 'override', label: 'Custom Flat Rate ($)', icon: 'DollarSign' },
  ];

  readonly driverDisplayName = computed(() => {
    const seg = this.targetSegment();
    const dId = this.driverId() || seg?.driverId || this.load?.driver1Id || '';
    if (!dId) return 'Assigned Driver';
    const fallback =
      (seg?.driverId === dId ? seg?.driverName : seg?.driver2Name) ||
      this.load?.driver1Name ||
      'Driver';
    return this.userService.getDriverDisplayName(dId, fallback);
  });

  readonly legSummaryLabel = computed(() => {
    const seg = this.targetSegment();
    const totalStops = this.load?.stops?.length || 2;
    if (seg) {
      return `Leg ${seg.segmentNumber} · Stops ${seg.fromStopNumber}–${seg.toStopNumber}`;
    }
    return `Leg 1 · Stops 1–${totalStops}`;
  });

  readonly loadedMiles = computed(() => {
    const seg = this.targetSegment();
    if (seg?.segmentMiles != null && seg.segmentMiles > 0) {
      return seg.segmentMiles;
    }
    return this.load?.loadedMiles || calculateTotalLoadedMiles(this.load?.stops || []);
  });

  readonly currentBasePayDisplay = computed<number | null>(() => {
    const dId = this.driverId() || this.targetSegment()?.driverId || this.load?.driver1Id;
    if (dId && this.load?.driverPaySnapshots?.[dId]?.basePay != null) {
      return this.load.driverPaySnapshots[dId].basePay;
    }
    if (this.load?.payrollSnapshot?.basePay != null) {
      return this.load.payrollSnapshot.basePay;
    }
    return null;
  });

  ngOnInit(): void {
    const data = this.modalService.state()?.data || {};
    this.load = data.load;
    if (Array.isArray(data.segments)) {
      this.segments.set(data.segments);
    }

    const seg: LoadSegment | null = data.segment || this.segments()[0] || null;
    this.targetSegment.set(seg);
    this.driverId.set(data.driverId || seg?.driverId || this.load?.driver1Id || '');

    const existingOverride =
      seg?.manualPayOverride != null && isFinite(Number(seg.manualPayOverride))
        ? Number(seg.manualPayOverride)
        : null;
    const existingEmptyOrigin =
      seg?.emptyMilesOrigin != null && Number(seg.emptyMilesOrigin) > 0
        ? Number(seg.emptyMilesOrigin)
        : null;
    const existingEmptyDest =
      seg?.emptyMilesDestination != null && Number(seg.emptyMilesDestination) > 0
        ? Number(seg.emptyMilesDestination)
        : null;

    this.payMode.set(existingOverride !== null ? 'override' : 'auto');
    this.hasExistingDeadhead.set(existingEmptyOrigin !== null || existingEmptyDest !== null);

    this.form = this.fb.group({
      manualPayOverride: [existingOverride],
      manualPayOverrideNote: [seg?.manualPayOverrideNote || ''],
      emptyMilesOrigin: [existingEmptyOrigin],
      emptyMilesDestination: [existingEmptyDest],
    });
  }

  onPayModeChange(mode: string): void {
    this.payMode.set(mode as LegPayCalculationMode);
  }

  async save(): Promise<void> {
    const val = this.form.getRawValue();
    const isOverride = this.payMode() === 'override';

    if (
      isOverride &&
      (val.manualPayOverride === null ||
        val.manualPayOverride === undefined ||
        isNaN(Number(val.manualPayOverride)) ||
        Number(val.manualPayOverride) < 0)
    ) {
      this.toastService.show(
        'warning',
        'Please enter a valid flat dollar amount for this leg.',
        { title: 'Amount Required' }
      );
      return;
    }

    const manualPayOverride = isOverride ? Number(val.manualPayOverride) : null;
    const manualPayOverrideNote = isOverride
      ? val.manualPayOverrideNote?.trim() || null
      : null;

    const emptyMilesOrigin =
      val.emptyMilesOrigin !== null &&
      val.emptyMilesOrigin !== undefined &&
      !isNaN(Number(val.emptyMilesOrigin)) &&
      Number(val.emptyMilesOrigin) > 0
        ? Number(val.emptyMilesOrigin)
        : null;

    const emptyMilesDestination =
      val.emptyMilesDestination !== null &&
      val.emptyMilesDestination !== undefined &&
      !isNaN(Number(val.emptyMilesDestination)) &&
      Number(val.emptyMilesDestination) > 0
        ? Number(val.emptyMilesDestination)
        : null;

    this.isSaving.set(true);
    try {
      const segId = this.targetSegment()?.id || '';
      await this.segmentService.updateLegAssignment(
        this.load,
        segId,
        {
          manualPayOverride,
          manualPayOverrideNote,
          emptyMilesOrigin,
          emptyMilesDestination,
        },
        this.segments(),
        isOverride
          ? `Set custom leg flat pay ($${manualPayOverride?.toFixed(2)})`
          : 'Updated leg pay / deadhead miles'
      );

      this.toastService.show(
        'success',
        'Leg pay settings saved. Driver pay breakdown will refresh automatically.',
        { title: 'Leg Pay Updated' }
      );
      this.modalService.state()?.onSuccess?.();
      this.modalService.close();
    } catch (err: any) {
      console.error('Failed to save leg pay:', err);
      this.toastService.show('error', err?.message || 'Failed to update leg pay.', {
        title: 'Save Failed',
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
